import { DIM_PREFIX } from './dashboardChartData';

// Which campaign a table row is about, so clicking it can open that campaign. Pure, so it is
// tested without rendering anything.

export type DashboardCampaignLink =
    | { kind: 'id'; id: string }
    // Only the campaign's reference or name is on the row: its id is looked up on click.
    | { kind: 'lookup'; field: 'reference' | 'name'; value: string };

const LOOKUP_FIELDS = ['reference', 'name'] as const;

// On the dashboard's own report `id` is the campaign's id and `name`/`reference` its name and
// reference. A saved datasource's columns mean whatever it says, so only an explicit
// `campaign_id` is trusted there.
export const campaignLinkOf = (
    row: Record<string, unknown>,
    dimensionFields: string[],
    defaultSource: boolean,
): DashboardCampaignLink | null => {
    const valueOf = (field: string): string | null => {
        if (!dimensionFields.includes(field)) return null;
        const value = row[`${DIM_PREFIX}${field}`];
        return value == null || String(value).trim() === '' ? null : String(value);
    };
    const id = valueOf('campaign_id') ?? (defaultSource ? valueOf('id') : null);
    if (id != null && /^\d+$/.test(id)) return { kind: 'id', id };
    if (!defaultSource) return null;
    const field = LOOKUP_FIELDS.find(f => valueOf(f) != null);
    const value = field ? valueOf(field) : null;
    return field && value != null ? { kind: 'lookup', field, value } : null;
};

type QueryFilter = { field: string; operator: string; value: string | number | string[] };

// The campaign a reference or name belongs to, within the filters the widget ran with (its
// period keeps the scan small, and the row came from inside them). The one with the most rows
// wins should two campaigns share a name.
export const campaignLookupRequest = (
    link: Extract<DashboardCampaignLink, { kind: 'lookup' }>,
    filters: QueryFilter[],
) => ({
    // Required, and the dynamic report only accepts bar/line/pie; the query doesn't read it.
    graphType: 'bar' as const,
    dimensions: [{ field: 'campaign_id' }],
    metrics: [{ field: 'campaign_id', aggregation: 'count' as const }],
    filters: [
        ...filters.filter(f => f.field !== link.field),
        { field: link.field, operator: 'eq', value: link.value },
    ],
    sort: [{ field: 'campaign_id_count', direction: 'desc' as const }],
    limit: 1,
});
